import type { ReactNode } from 'react'

import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import { CARD_SURFACE, SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

/*
 * Brokers · By broker before its first response: the LOADED page, block for
 * block, in its order — the same rule Market's own `MarketBoardStates.tsx`
 * sets out. Every block below is the loaded card's OWN canonical `skeleton`
 * (copied out of `BrokerHeaderCard.tsx`, `BrokerPerformanceCard.tsx`,
 * `BrokerVsMarketCard.tsx` and `BrokerHoldingsCard.tsx` — this file cannot
 * import those, route `pendingComponent`s are not code-split), inside the
 * loaded card's own `SectionCard`/`section` chrome, so a loaded card replacing
 * its own skeleton block never moves anything below it. The tables are
 * `DataTableSkeleton`, the same light module their cards draw while loading.
 *
 * The blocks, top to bottom, on the "overview" sub-tab a fresh URL opens on (the revamp mock):
 *
 *   controls row        the broker chip + the period segments and 📅
 *   header panel        the 60 mark and name row, the six-cell KPI row, the split bar
 *   performance card    the card's own loading shape under a fixed `min-h-55`
 *   stock tables        titles on the canvas over bordered tables: two side by side, one full
 *   vs-market card      four inset cells
 *   holdings card       two 272 strip maps under their labels
 *   stock activity      its card (title, count, search, table), then the pager outside it
 *   peers card          five ranked rows
 *
 * Every row count inside a card (the stock tables' 5-per-side) is that card's
 * OWN skeleton's typical count, not this file's guess.
 *
 * Bars only, no words, so nothing here can be mistaken for, or found by a
 * test as, the loaded content. The page's own title is the exception, and
 * `BrokerScreen`'s pending page draws it via the real `TitleRow`.
 */

/** `BrokerControls`: the broker chip + the shared period picker, one row. */
function ControlsSkeleton() {
  return (
    <div className="flex flex-col gap-3 @md:flex-row @md:flex-wrap @md:items-center @md:justify-between">
      <Skeleton className="h-11 w-44 rounded-sm lg:h-9" />
      <Skeleton className="h-9 w-72 rounded-sm" />
    </div>
  )
}

/** The mock's padded panel (head, performance, vs market): 20 / 24 / 24. */
const PANEL = cn(CARD_SURFACE, 'px-6 pt-5 pb-6 @max-sm:p-4')

/** `BrokerHeaderCard`'s own `skeleton` prop, copied into its box. */
function HeaderCardSkeleton() {
  return (
    <div className={cn(PANEL, '@container flex flex-col')}>
      <div className="flex items-center gap-4 pb-4">
        <Skeleton className="size-15 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-7 w-56 rounded-sm" />
          <Skeleton className="h-4 w-32 rounded-xs" />
        </div>
        <Skeleton className="h-4 w-32 shrink-0 rounded-xs" />
      </div>
      <div className="grid grid-cols-2 gap-y-4 border-y border-border-subtle py-4 @md:grid-cols-3 @2xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => (
          <div key={index} className="flex flex-col gap-1 pr-4">
            <Skeleton className="h-4 w-20 rounded-xs" />
            <Skeleton className="h-6 w-24 rounded-xs" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2 pt-4">
        <Skeleton className="h-4 w-full rounded-xs" />
        <Skeleton className="h-2 w-full rounded-none" />
      </div>
    </div>
  )
}

/** A card's 36px head row with a bar where its title goes. */
function CardHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex min-h-9 items-center justify-between gap-3">
      <Skeleton className={cn('h-4 rounded-xs', title)} />
      {children}
    </div>
  )
}

/** `BrokerPerformanceCard`: its head with the `TabBar` at the right, then its own `skeleton`. */
function PerformanceCardSkeleton() {
  return (
    <div className={cn(PANEL, '@container flex flex-col gap-4')}>
      <CardHead title="w-40">
        <div className="flex items-center gap-5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-4 w-16 rounded-xs" />
          ))}
        </div>
      </CardHead>
      <div className="flex min-h-55 flex-col gap-6 @2xl:flex-row">
        <Skeleton className="h-44 w-full rounded-md" />
        <Skeleton className="h-44 w-full rounded-md" />
      </div>
    </div>
  )
}

/** One `FlowTable`: its title on the canvas, then the bordered table's five two-line rows. */
function FlowTableSkeleton({ title }: { title: string }) {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className={cn('h-5 rounded-xs', title)} />
      <DataTableSkeleton rows={5} columns={4} rowHeight="lg" />
    </div>
  )
}

/** `BrokerStockTables`: bought/sold side by side, net full width below. */
function StockTablesSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 @2xl:grid-cols-2 @2xl:items-start @2xl:gap-5">
        <FlowTableSkeleton title="w-32" />
        <FlowTableSkeleton title="w-28" />
      </div>
      <FlowTableSkeleton title="w-40" />
    </div>
  )
}

/** `BrokerVsMarketCard`'s own `skeleton`: four inset cells. */
function VsMarketCardSkeleton() {
  return (
    <div className={cn(PANEL, '@container flex flex-col gap-3')}>
      <CardHead title="w-44" />
      <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-4 @2xl:gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col gap-2 bg-muted px-4 py-3">
            <Skeleton className="h-4 w-20 rounded-xs" />
            <Skeleton className="h-7 w-16 rounded-xs" />
            <Skeleton className="h-4 w-24 rounded-xs" />
          </div>
        ))}
      </div>
    </div>
  )
}

/** `BrokerHoldingsCard`'s own `skeleton`: each group's label over its 272 map. */
function HoldingsCardSkeleton() {
  return (
    <div className={cn(SECTION_CARD, 'flex flex-col gap-3')}>
      <Skeleton className="h-5 w-48 rounded-xs" />
      {[0, 1].map((group) => (
        <div key={group} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-24 rounded-xs" />
          <Skeleton className="h-68 w-full rounded-none" />
        </div>
      ))}
    </div>
  )
}

/** `BrokerStockActivityCard`: title and count, the search at the right, the table, then the pager outside. */
function StockActivityCardSkeleton() {
  return (
    <>
      <div className={cn(SECTION_CARD, 'flex flex-col gap-3')}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Skeleton className="h-5 w-52 rounded-xs" />
            <Skeleton className="h-4 w-40 rounded-xs" />
          </div>
          <Skeleton className="h-11 w-65 rounded-sm" />
        </div>
        <DataTableSkeleton bare rows={5} columns={6} rowHeight="lg" />
      </div>
      <Skeleton className="h-9 w-full rounded-sm" />
    </>
  )
}

/** `BrokerPeersCard`: head, then the table's five rows. */
function PeersCardSkeleton() {
  return (
    <div className={SECTION_CARD}>
      <CardHead title="w-44" />
      <DataTableSkeleton bare rows={5} columns={4} className="mt-3" />
    </div>
  )
}

export function BrokerBoardSkeleton() {
  return (
    <>
      <ControlsSkeleton />
      {/* No `WindowNote` placeholder: since the owner's 2026-09-30 ruling
          against a routine "As of …" caption (`BrokerScreen`'s
          `hasPartialWindow` gate, mirroring Overview's `FloorStatsStrip`),
          the common `today`/fully-caught-up read draws nothing there at all
          — a bar here would be a line the loaded page usually never grows
          into. */}
      <HeaderCardSkeleton />
      <PerformanceCardSkeleton />
      <StockTablesSkeleton />
      <VsMarketCardSkeleton />
      <HoldingsCardSkeleton />
      <StockActivityCardSkeleton />
      <PeersCardSkeleton />
    </>
  )
}
